import { ApiHolder, githubAuthApiRef, useApi, useApiHolder } from '@backstage/core-plugin-api';
import { FormControl, TextField } from '@material-ui/core';
import { useCallback, useEffect } from 'react';
import { useAsync } from 'react-use';
import Autocomplete, { AutocompleteChangeReason } from '@material-ui/lab/Autocomplete';
import Alert from '@material-ui/lab/Alert';
import { catalogApiRef, humanizeEntityRef } from '@backstage/plugin-catalog-react';
import { Entity, parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { unifiedGithubApiRef } from '../../services';
import { GithubOrgPrefix } from '../../components/GithubOrgPrefix';
import { FieldValidation } from '@rjsf/utils';
import { getGithubDestination } from './ComponentInformation/githubDestination';
import { entityMatchesGithubTeam, isInvalidGithubTeam } from '../../components/GithubTeamSelect/githubTeamMatching';

export const GithubTeamField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const apiHolder = useApiHolder();
  const configuredOrganization = uiSchema?.['ui:options']?.githubOrganization;
  const githubDestination = getGithubDestination(configuredOrganization, apiHolder);
  const destination = githubDestination.status === 'resolved' ? githubDestination.destination : undefined;
  const destinationError = githubDestination.status === 'error' ? githubDestination.message : undefined;
  const unifiedGithubApi = useApi(unifiedGithubApiRef);
  const catalogApi = useApi(catalogApiRef);
  const ghecAuthApi = useApi(githubAuthApiRef);

  const {
    value: entities,
    loading,
    error,
  } = useAsync(async () => {
    if (!destination) {
      throw new Error(destinationError);
    }
    const backstageUserIdentity = await ghecAuthApi.getBackstageIdentity();

    if (!backstageUserIdentity?.identity?.userEntityRef) {
      throw new Error('No GitHub username found for this account.');
    }

    const { name: githubUsername } = parseEntityRef(backstageUserIdentity?.identity.userEntityRef as string);
    const [{ items }, githubTeams] = await Promise.all([
      catalogApi.getEntities({ filter: { kind: ['Group'] } }),
      unifiedGithubApi.getUsersGithubTeams({ githubUsername, origin: destination.origin }),
    ]);

    const eligibleTeams = githubTeams.filter(
      team => !isInvalidGithubTeam(team) && team.org?.toLowerCase() === destination.organization.toLowerCase(),
    );
    const final = items.filter(e => eligibleTeams.some(team => entityMatchesGithubTeam(e, team)));

    if (final.length === 0) {
      throw new Error(
        `No eligible GitHub teams found in ${destination.organization}. Please request access to an eligible GitHub team in ${destination.organization} and try again.`,
      );
    }

    return { entities: final, teams: eligibleTeams };
  }, [destination?.organization, destination?.origin, destinationError]);

  const hasDestination = destination !== undefined;
  useEffect(() => {
    if (
      hasDestination &&
      !loading &&
      formData &&
      !entities?.entities.some(entity => stringifyEntityRef(entity) === formData)
    ) {
      onChange(undefined);
    }
  }, [hasDestination, entities, loading, formData, onChange]);

  const onSelect = useCallback(
    (_: unknown, ref: string | Entity | null, reason: AutocompleteChangeReason) => {
      // ref can either be a string from free solo entry or
      if (typeof ref !== 'string') {
        // if ref does not exist: pass 'undefined' to trigger validation for required value
        onChange(ref ? stringifyEntityRef(ref as Entity) : undefined);
      } else if (reason === 'blur' || reason === 'create-option') {
        // Add in default namespace, etc.
        let entityRef = ref;
        try {
          // Attempt to parse the entity ref into it's full form.
          entityRef = stringifyEntityRef(
            parseEntityRef(ref as string, {
              defaultKind: 'Group',
              defaultNamespace: 'Group',
            }),
          );
        } catch (err) {
          // If the passed in value isn't an entity ref, do nothing.
        }
        // We need to check against formData here as that's the previous value for this field.
        if (formData !== ref) {
          onChange(entityRef);
        }
      }
    },
    [onChange, formData],
  );

  if (!destination) {
    return <Alert severity="error">{destinationError}</Alert>;
  }

  return (
    <FormControl margin="normal" required={required} error={rawErrors?.length > 0 && !formData}>
      <Autocomplete
        id={schema.$id}
        value={entities?.entities.find(e => stringifyEntityRef(e) === formData)}
        loading={loading}
        onChange={onSelect}
        options={entities?.entities || []}
        autoSelect
        freeSolo={false}
        getOptionLabel={option =>
          typeof option === 'string' ? option : humanizeEntityRef(option, { defaultKind: 'Group' })!
        }
        renderInput={params => (
          <TextField
            {...params}
            label="GitHub Team"
            margin="dense"
            error={error !== undefined}
            helperText={error ? error.message : 'Select a GitHub Team'}
            FormHelperTextProps={{ margin: 'dense', style: { marginLeft: 0 } }}
            variant="outlined"
            required={required}
            InputProps={{
              ...params.InputProps,
              startAdornment: <GithubOrgPrefix organization={destination.organization} />,
            }}
          />
        )}
      />
    </FormControl>
  );
};

export const githubTeamFieldValidation = (
  _value: string,
  validation: FieldValidation,
  context?: { apiHolder?: ApiHolder; uiSchema?: { 'ui:options'?: Record<string, unknown> } },
) => {
  const githubDestination = getGithubDestination(
    context?.uiSchema?.['ui:options']?.githubOrganization,
    context?.apiHolder,
  );
  if (githubDestination.status === 'error') {
    validation.addError(githubDestination.message);
  }
};
